import type { ReactNode } from 'react'
import { BrandPanel } from '@/components/brand/brand-panel'
import { DecorativeBackground } from '@/components/brand/decorativeBackground'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'

const WIDTHS = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-xl',
  xl: 'max-w-3xl',
} as const

export function OnboardingLayout({
  eyebrow,
  title,
  description,
  step,
  totalSteps,
  width = 'md',
  brandPanel = false,
  fitContent = false,
  footer,
  below,
  trackStatusLink,
  children,
}: {
  eyebrow?: string
  title: string
  description?: string
  step?: number
  totalSteps?: number
  width?: keyof typeof WIDTHS
  /** Split layout with the brand video panel — used by Login/TeamLogin, not the onboarding funnel. */
  brandPanel?: boolean
  /** Size the card to its content instead of the shared fixed height — for steps with too little content to fill it. */
  fitContent?: boolean
  footer?: ReactNode
  below?: ReactNode
  /** An opt-in "Track My Status" entry point, rendered via `CardAction` in the card header's
   * top-right corner (next to the eyebrow) — e.g. `<TrackMyStatusDialog />` (`@/features/public`). A
   * `ReactNode` slot rather than a boolean that builds the dialog in here: this component
   * intentionally has no dependency on `features/public` (which itself already depends on
   * `features/onboarding` for this very layout), so each caller passes its own trigger instead of
   * this layout importing it and creating a cross-feature cycle. Omitted by default — every
   * existing page using this layout is unaffected. */
  trackStatusLink?: ReactNode
  children?: ReactNode
}) {
  const pct = step && totalSteps ? Math.round((step / totalSteps) * 100) : null

  const content = (
    <div className={cn('relative z-10 flex w-full flex-col gap-3', WIDTHS[width])}>
      {pct !== null && (
        <div className="flex flex-col gap-1.5">
          <div className="text-muted-foreground flex items-center justify-between text-xs font-medium">
            <span>
              Step {step} of {totalSteps}
            </span>
            <span className="tabular-nums">{pct}%</span>
          </div>
          <Progress value={pct} className="bg-primary/15 h-1.5" />
        </div>
      )}

      <Card
        className={fitContent ? 'max-h-[calc(100svh-9rem)]' : 'h-[min(34rem,calc(100svh-9rem))]'}
      >
        <CardHeader className="shrink-0">
          {eyebrow && (
            <CardDescription className="text-primary text-xs font-semibold uppercase tracking-wide">
              {eyebrow}
            </CardDescription>
          )}
          <CardTitle className="text-xl font-bold tracking-tight">
            {/* A real <h1> for document semantics — CardTitle itself renders a plain <div>. The
                inline style makes it inherit CardTitle's own visual styling exactly (font
                shorthand + letter-spacing + color), so this is semantics-only, no visual change. */}
            <h1 style={{ margin: 0, font: 'inherit', letterSpacing: 'inherit', color: 'inherit' }}>
              {title}
            </h1>
          </CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
          {trackStatusLink && <CardAction>{trackStatusLink}</CardAction>}
        </CardHeader>

        <CardContent
          className={
            fitContent
              ? // `fitContent` sizes the card snugly to its content with no slack, so a
                // button's `:active` press transform (translate-y-px, from the shared
                // Button component) can momentarily push the scrollable overflow area 1px
                // past the container — `overflow-y-auto` would flash a scrollbar for that
                // one frame. `hidden` avoids it; there's no real content to scroll to here.
                'overflow-hidden'
              : 'min-h-0 flex-1 overflow-y-auto overscroll-contain'
          }
        >
          {children}
        </CardContent>

        {footer && (
          <CardFooter className="shrink-0 flex-col items-stretch gap-3">{footer}</CardFooter>
        )}
      </Card>

      {below}
    </div>
  )

  if (brandPanel) {
    return (
      <div className="bg-muted flex min-h-svh flex-col font-[family-name:var(--font-display)] md:flex-row md:gap-4 md:p-4">
        <BrandPanel />
        <main className="bg-card relative flex flex-1 basis-0 flex-col items-center justify-center overflow-hidden overflow-y-auto rounded-2xl px-4 py-8 shadow-md md:h-[calc(100svh-2rem)] md:min-w-0 md:px-6">
          <DecorativeBackground src="/img/checkup-bg.webp" />
          <div className="bg-card/50 pointer-events-none absolute inset-0 z-0" />
          {content}
        </main>
      </div>
    )
  }

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center overflow-x-hidden px-4 py-8 font-[family-name:var(--font-display)] md:px-6">
      <DecorativeBackground src="/img/checkup-bg.webp" />
      <div className="bg-background/75 pointer-events-none fixed inset-0 z-0" aria-hidden="true" />
      {content}
    </div>
  )
}
